import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CapiProductPage } from 'types/capi/pdp';

import ArrowIcon from '~/assets/icons/Arrow';
import ChevronIcon from '~/assets/icons/Chevron';
import SizeInfo from '~/components/SizeInfo';
import SizeSelectionEvent from '~/events/SizeSelectionEvent';
import useHasScrolledIntoView from '~/hooks/useHasScrolledIntoView';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useSizeInteraction from '~/hooks/useSizeInteraction';

import SizeSelectorGrid from './SizeSelectorGrid';

type Props = {
  forceExpandedSizeGrid?: boolean;
  inPanel?: boolean;
  isExpanded: boolean;
  selectedSize?: string;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedSize: (size?: string) => void;
  shouldShowSizeGrid: boolean;
  sizes: string[];
};

export default function SizeSelection({
  forceExpandedSizeGrid,
  inPanel,
  selectedSize,
  shouldShowSizeGrid,
  setSelectedSize,
  setIsExpanded,
  isExpanded,
  sizes
}: Props) {
  const product = usePageData<CapiProductPage>();
  const { isBot } = useRootData();
  const { t } = useTranslation('pdp');
  const region = useRegion();
  const sendSizeInteraction = useSizeInteraction();

  const onGridToggleClick = () => {
    setIsExpanded(!isExpanded);
  };

  const onSizeSelect = (size: string) => {
    setSelectedSize(size);
    setIsExpanded(!size);
    new SizeSelectionEvent({ isBot, pageType: 'pdp' }).send();
    sendSizeInteraction(size);
  };

  const isExpandedOverride = Boolean(isExpanded || forceExpandedSizeGrid);

  const gridRef = useRef<HTMLDivElement>(null);
  const hasGridScrolledIntoView = useHasScrolledIntoView(gridRef);

  if (shouldShowSizeGrid && inPanel) {
    const hasSizeGuide = Boolean(
      product.size_fit_guide && region.code === 'GB'
    );

    return (
      <div className="flex flex-col gap-4">
        {!selectedSize && (
          <div className="overflow-hidden">
            <div
              className={`flex gap-2 bg-yellow-label p-2 text-sm tracking-wide opacity-0 ${hasGridScrolledIntoView ? 'animate-slide-delay' : ''}`}
            >
              <span className="rotate-270">
                <ArrowIcon className="animate-pulse" />
              </span>
              <span>{t('selectYourSizeTooltip')}</span>
            </div>
          </div>
        )}
        {!isExpandedOverride && (
          <button
            className="mx-4 rounded-full border border-grey-divide bg-white px-4 py-2 text-sm text-black"
            onClick={onGridToggleClick}
          >
            {t('showAllSizes')}
          </button>
        )}
        <div className="flex flex-col" ref={gridRef}>
          <div
            className={`flex flex-col gap-4 bg-white py-4 ${hasSizeGuide ? 'rounded-t-lg' : 'rounded-lg'}`}
          >
            <SizeSelectorGrid
              onSizeSelect={onSizeSelect}
              selectedSize={selectedSize}
              isExpanded={isExpandedOverride}
              sizes={sizes}
            />
          </div>
          {hasSizeGuide && product.size_fit_guide && (
            <div
              className={`flex w-full flex-col items-center justify-center gap-4 rounded-b-lg px-4 pb-4 ${product.size_fit_guide.adjustment === 0 ? 'bg-white' : 'bg-yellow-label pt-4'}`}
            >
              <SizeInfo sizeFitGuide={product.size_fit_guide} />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (shouldShowSizeGrid) {
    return (
      <>
        <div className="flex items-center justify-between">
          <h2 className="text-title-sm">{t('selectYourSize')}</h2>
          {!isExpandedOverride && (
            <GridToggle
              label={t('expandAllSizes')}
              onClick={onGridToggleClick}
            />
          )}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            {product.size_fit_guide && region.code === 'GB' && (
              <div
                className={`flex w-full flex-col items-center justify-center gap-4 ${product.allow_size_selection ? 'rounded-t-lg px-4 pt-4' : 'rounded-lg p-4'} ${product.size_fit_guide?.adjustment === 0 ? 'bg-grey-panel' : 'bg-yellow-label p-4'}`}
              >
                <SizeInfo sizeFitGuide={product.size_fit_guide} />
              </div>
            )}
            <div
              className={`flex flex-col gap-4 bg-grey-panel py-4 ${product.size_fit_guide ? 'rounded-b-lg' : 'rounded-lg'}`}
            >
              <SizeSelectorGrid
                onSizeSelect={onSizeSelect}
                selectedSize={selectedSize}
                isExpanded={isExpandedOverride}
                sizes={sizes}
              />
            </div>
          </div>
          {!selectedSize && (
            <div className="flex gap-2 bg-yellow-label p-2 text-sm tracking-wide">
              <span className="rotate-90">
                <ArrowIcon />
              </span>
              <span>{t('selectYourSizeTooltip')}</span>
            </div>
          )}
        </div>
      </>
    );
  }

  if (region.code === 'GB') return <SimplifiedSizeGuide product={product} />;
}

function SimplifiedSizeGuide({ product }: { product: CapiProductPage }) {
  if (!product.size_fit_guide) return null;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-title-sm">Size guide</h2>
      <div
        className={`flex w-full flex-col items-center justify-center gap-4 rounded-lg p-4 ${product.size_fit_guide?.adjustment.toString() === '0' ? 'bg-grey-tile' : 'bg-yellow-label p-4'}`}
      >
        <SizeInfo sizeFitGuide={product.size_fit_guide} />
      </div>
    </div>
  );
}

function GridToggle({
  label,
  onClick
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button className="flex items-center gap-1" onClick={onClick}>
      <div className="text-xs tracking-wide text-grey-hide underline">
        {label}
      </div>
      <ChevronIcon height="6" stroke="#A9A9A9" width="9" />
    </button>
  );
}
